import { css } from "@emotion/react";
import { Button, Theme, useTheme } from "@mui/material";
import { FiHash } from "react-icons/fi";
import { IoFilter } from "react-icons/io5";
import { PiSortAscendingBold } from "react-icons/pi";

import {
  ORDER_TYPE,
  QUEST_PROGRESS_STATUSES,
  QuestOrder,
  QuestState,
} from "~/components/category/[id]/quest/lib";
import SingleSelectInput from "~/components/common/SingleSelectInput";
import {
  QUEST_DURATION_STATUSES,
  QuestDurationStatus,
  questDurationLabel,
} from "~/domains/quest/lib";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import { engFont, font } from "~/lib/client/fonts";
import {
  LoggableParams,
  useActivityLogger,
} from "~/lib/client/logger/useActivityLogger";
import { CategoryDetailQuery } from "~/lib/graphql/client";
import { useCategoryDetailContext } from "~/pages/category/[id]";

const QuestTabHead = ({
  selectedQuestStatus,
  setSelectedQuestStatus,
  selectedSort,
  setSelectedSort,
  selectedDurationStatus,
  setSelectedDurationStatus,
  questGenreTags,
  selectedQuestGenreTags,
  setSelectedQuestGenreTags,
}: {
  selectedQuestStatus: QuestState[];
  setSelectedQuestStatus: (status: QuestState[]) => void;
  selectedSort: QuestOrder;
  setSelectedSort: (order: QuestOrder) => void;
  selectedDurationStatus: QuestDurationStatus;
  setSelectedDurationStatus: (status: QuestDurationStatus) => void;
  questGenreTags: CategoryDetailQuery["category"]["quests"][number]["questGenreTags"];
  selectedQuestGenreTags: CategoryDetailQuery["category"]["quests"][number]["questGenreTags"];
  setSelectedQuestGenreTags: (
    tags: CategoryDetailQuery["category"]["quests"][number]["questGenreTags"]
  ) => void;
}) => {
  const theme = useTheme();
  const sendActivityLogger = useActivityLogger();
  const { props } = useCategoryDetailContext();

  const onClickRecruitQuestButton = async () => {
    if (!sendActivityLogger) return;

    const params: LoggableParams = {
      actionName: "click_recruit_quest_button",
      pageName: "categoryDetail",
      categoryId: props.category.id,
    };
    await sendActivityLogger(params);
  };

  return (
    <div css={rootStyle(theme)}>
      <div
        css={questDurationStatusSelectContainerStyle(theme)}
        className={font.className}
      >
        {QUEST_DURATION_STATUSES.map((status, index) => (
          <div
            key={index}
            role="button"
            tabIndex={0}
            onClick={() => setSelectedDurationStatus(status)}
            onKeyDown={(event) => {
              if (event.code === "Enter" || event.code === "Space") {
                setSelectedDurationStatus(status);
              }
            }}
            css={questDurationStatusSelectorStyle(
              theme,
              status,
              selectedDurationStatus
            )}
            className={engFont.className}
          >
            {questDurationLabel(status)}
          </div>
        ))}
      </div>
      <div css={filterContainerListContainerStyle}>
        {/*並び順に関するコントローラー*/}
        <div css={filterContainerStyle}>
          <PiSortAscendingBold size={"1.2rem"} />
          <div css={filterButtonContainerStyle}>
            {ORDER_TYPE.map((order, index) => (
              <SingleSelectInput
                label={order.label}
                selected={selectedSort === order}
                setSelected={() => setSelectedSort(order)}
                key={index}
              />
            ))}
          </div>
        </div>

        {/*ユーザのクエスト進行度ステータスに関するフィルタ*/}
        <div css={filterContainerStyle}>
          <IoFilter size={"1.2rem"} />
          <div css={filterButtonContainerStyle}>
            {QUEST_PROGRESS_STATUSES.map((status, index) => (
              <div
                role={"button"}
                css={selectableItemStyle(
                  theme,
                  selectedQuestStatus.includes(status)
                )}
                onClick={() => {
                  if (selectedQuestStatus.includes(status)) {
                    setSelectedQuestStatus(
                      selectedQuestStatus.filter((s) => s !== status)
                    );
                  } else {
                    setSelectedQuestStatus([...selectedQuestStatus, status]);
                  }
                }}
                key={index}
              >
                {status.label}
              </div>
            ))}
          </div>
        </div>
        {/*クエストのタグに関するコントローラー*/}
        <div css={filterContainerStyle}>
          <FiHash size={"1.2rem"} />
          <div css={filterButtonContainerStyle}>
            {questGenreTags.map((tag, index) => (
              <div
                key={index}
                role={"button"}
                onClick={() => {
                  if (selectedQuestGenreTags.includes(tag)) {
                    setSelectedQuestGenreTags(
                      selectedQuestGenreTags.filter((t) => t.id !== tag.id)
                    );
                  } else {
                    setSelectedQuestGenreTags([...selectedQuestGenreTags, tag]);
                  }
                }}
                css={selectableItemStyle(
                  theme,
                  selectedQuestGenreTags.includes(tag)
                )}
              >
                {tag.name}
              </div>
            ))}
          </div>
        </div>
        <Button
          variant="contained"
          color="primary"
          href={
            "https://docs.google.com/forms/d/1caxulb5US-oZ_Y6HScXU1BXlo-LaH35WX0mkqreruLA"
          }
          onClick={onClickRecruitQuestButton}
          sx={{ mr: 1 }}
        >
          クエストを提案する
        </Button>
      </div>
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  background: ${theme.palette.background.default};
  border-bottom: 1px solid ${theme.palette.divider};
`;

const questDurationStatusSelectContainerStyle = (theme: Theme) => css`
  display: flex;
  gap: 4rem;
  font-size: 18px;
  font-weight: 700;
  border-bottom: 1px solid ${theme.palette.divider};

  ${mediaQueryIsMobile(css`
    margin-top: 2rem;
    padding: 0.75rem 2rem;
  `)};

  ${mediaQueryIsDesktop(css`
    margin-top: 4rem;
    padding: 2rem 2rem 0 2rem;
  `)};
`;

const questDurationStatusSelectorStyle = (
  theme: Theme,
  status: QuestDurationStatus,
  selectedDurationStatus: QuestDurationStatus
) => css`
  color: ${selectedDurationStatus === status
    ? theme.palette.text.primary
    : theme.palette.text.disabled};
  border-bottom: ${selectedDurationStatus === status
    ? `2px solid ${theme.palette.primary.main}`
    : "none"};
  font-weight: 700;

  &:hover {
    cursor: pointer;
  }
`;

const filterContainerListContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  padding: 1rem 0 1rem 1rem;

  ${mediaQueryIsMobile(css`
    gap: 0.5rem;
    margin-left: 0;

    // 最後の要素だけ右端に寄せる
    > :last-child {
      margin-left: auto;
    }
  `)};

  ${mediaQueryIsDesktop(css`
    align-items: center;
    gap: 2rem;
    margin-left: 1rem;
    // 最後の要素だけ右端に寄せる
    > :last-child {
      margin-left: auto;
    }
  `)};
`;

const filterContainerStyle = css`
  display: flex;
  gap: 0.5rem;
  font-size: 12px;
  flex-wrap: wrap;
  align-items: flex-start;

  ${mediaQueryIsMobile(css`
    width: 100%;
  `)};
`;

const filterButtonContainerStyle = css`
  flex: 1;
  display: flex;
  gap: 4px;
  overflow-x: auto;

  ${mediaQueryIsMobile(css`
    padding-bottom: 0.5rem;
  `)};
`;

const selectableItemStyle = (theme: Theme, selected: boolean) => css`
  color: ${selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.disabled};
  background: ${selected
    ? theme.palette.primary.main
    : theme.palette.background.paper};
  padding: 6px 24px 6px 24px;
  border-radius: 52px;
  border: 1px solid ${theme.palette.divider};
  font-size: 0.75rem;
  line-height: 0.75rem;

  display: flex;
  height: 1.625rem;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
`;

export default QuestTabHead;
